export const ADMIN_TIME_ZONE = "Europe/Madrid";

const wallClockFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: ADMIN_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

function partsInMadrid(date: Date) {
  const parts = Object.fromEntries(
    wallClockFormatter.formatToParts(date).map((part) => [part.type, part.value]),
  );
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
}

function madridOffsetMs(date: Date): number {
  const parts = partsInMadrid(date);
  const madridAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  return madridAsUtc - date.getTime();
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

export function parseApiDate(value: string): Date {
  const hasExplicitZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(value);
  return new Date(hasExplicitZone ? value : `${value}Z`);
}

/** Converts an ISO 8601 UTC timestamp to the Madrid wall-clock value <input type="datetime-local"> expects. */
export function toDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = parseApiDate(iso);
  if (Number.isNaN(date.getTime())) return "";
  const parts = partsInMadrid(date);
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;
}

/** Converts a Madrid wall-clock <input type="datetime-local"> value back to an ISO 8601 UTC timestamp. */
export function fromDatetimeLocalValue(value: string): string | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  let utcMs = Date.UTC(year, month - 1, day, hour, minute);

  // The panel's official wall clock is Europe/Madrid, independent of browser/device zone.
  for (let i = 0; i < 3; i += 1) {
    utcMs = Date.UTC(year, month - 1, day, hour, minute) - madridOffsetMs(new Date(utcMs));
  }

  const date = new Date(utcMs);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

export function getNowDatetimeLocalValue(): string {
  return toDatetimeLocalValue(new Date().toISOString());
}

export function isPastDatetimeLocalValue(value: string, toleranceMs = 60_000): boolean {
  const iso = fromDatetimeLocalValue(value);
  if (!iso) return false;
  return new Date(iso).getTime() < Date.now() - toleranceMs;
}
